"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { getAppMetadata, type AppMetadata } from "./app-metadata";
interface AppStat {
  source: string;
  totalTokens: number;
  totalRequests: number;
  lastUsedAt: string | null;
}
interface AppsGridProps {
  apps: AppStat[];
}
interface RankedApp extends AppStat {
  rank: number;
  meta: AppMetadata;
}
const CATEGORIES: Array<{
  value: AppMetadata["category"] | "all";
  label: string;
}> = [{
  value: "all",
  label: "All"
}, {
  value: "coding",
  label: "Coding agents"
}, {
  value: "automation",
  label: "Automation"
}, {
  value: "other",
  label: "Other"
}];
const numberFormatter = new Intl.NumberFormat("en-US");
function formatTokens(n: number): string {
  if (n >= 1_000_000_000) {
    return `${(n / 1_000_000_000).toFixed(2)}B`;
  }
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(2)}M`;
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(1)}K`;
  }
  return numberFormatter.format(n);
}
function formatRelative(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) {
    return "just now";
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}d ago`;
  }
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}
function AppLogo(t0) {
  const $ = _c(16);
  const {
    Icon,
    name,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const dim = size === "lg" ? "h-14 w-14" : size === "sm" ? "h-9 w-9" : "h-12 w-12";
  const iconDim = size === "lg" ? "h-8 w-8" : size === "sm" ? "h-5 w-5" : "h-7 w-7";
  if (Icon) {
    let t2;
    if ($[0] !== dim) {
      t2 = cn("flex items-center justify-center rounded-xl bg-muted text-foreground border border-border/40", dim);
      $[0] = dim;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== Icon || $[3] !== iconDim) {
      t3 = <Icon className={iconDim} />;
      $[2] = Icon;
      $[3] = iconDim;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t2 || $[6] !== t3) {
      t4 = <div className={t2}>{t3}</div>;
      $[5] = t2;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t2;
  if ($[8] !== name) {
    t2 = name.replace(/^https?:\/\//, "").charAt(0).toUpperCase();
    $[8] = name;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const initial = t2;
  const t3 = size === "lg" ? "text-xl" : "text-base";
  let t4;
  if ($[10] !== dim || $[11] !== t3) {
    t4 = cn("flex items-center justify-center rounded-xl bg-muted border border-border/40 font-display font-semibold text-muted-foreground", dim, t3);
    $[10] = dim;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = initial || "?";
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function PodiumCard(t0) {
  const $ = _c(84);
  const {
    app,
    maxTokens,
    tone
  } = t0;
  const widthPct = maxTokens > 0 ? app.totalTokens / maxTokens * 100 : 0;
  let t1;
  if ($[0] !== app.lastUsedAt) {
    t1 = formatRelative(app.lastUsedAt);
    $[0] = app.lastUsedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const lastUsed = t1;
  let T0;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== app.meta.Icon || $[3] !== app.meta.description || $[4] !== app.meta.displayName || $[5] !== app.meta.url || $[6] !== app.rank || $[7] !== app.source || $[8] !== app.totalTokens || $[9] !== tone) {
    const toneStyles = {
      gold: {
        ring: "ring-blue-500/40 dark:ring-blue-400/30",
        rankColor: "text-blue-500 dark:text-blue-400",
        glow: "bg-blue-500/[0.08] dark:bg-blue-500/[0.06]",
        bar: "bg-blue-500 dark:bg-blue-400"
      },
      silver: {
        ring: "ring-border",
        rankColor: "text-foreground",
        glow: "bg-foreground/[0.04]",
        bar: "bg-foreground"
      },
      bronze: {
        ring: "ring-border",
        rankColor: "text-foreground",
        glow: "bg-foreground/[0.03]",
        bar: "bg-foreground"
      }
    }[tone];
    const Wrap = app.meta.url ? "a" : "div";
    let t12;
    if ($[21] !== app.meta.url) {
      t12 = app.meta.url ? {
        href: app.meta.url,
        target: "_blank" as const,
        rel: "noopener noreferrer"
      } : {};
      $[21] = app.meta.url;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    const wrapProps = t12;
    T0 = Wrap;
    t9 = wrapProps;
    t10 = cn("group relative block overflow-hidden rounded-2xl border bg-card p-6 ring-1 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl", toneStyles.ring);
    t11 = <div aria-hidden={true} className={cn("pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl", toneStyles.glow)} />;
    t6 = "relative space-y-5";
    let t13;
    if ($[23] !== app.meta.Icon || $[24] !== app.meta.displayName) {
      t13 = <AppLogo Icon={app.meta.Icon} name={app.meta.displayName} size="lg" />;
      $[23] = app.meta.Icon;
      $[24] = app.meta.displayName;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    let t14;
    if ($[26] !== app.meta.displayName) {
      t14 = <h3 className="font-display text-xl font-bold tracking-tight">{app.meta.displayName}</h3>;
      $[26] = app.meta.displayName;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    let t15;
    if ($[28] !== app.source) {
      t15 = <p className="font-mono text-xs text-muted-foreground truncate">{app.source}</p>;
      $[28] = app.source;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    let t16;
    if ($[30] !== t14 || $[31] !== t15) {
      t16 = <div className="min-w-0">{t14}{t15}</div>;
      $[30] = t14;
      $[31] = t15;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    let t17;
    if ($[33] !== t13 || $[34] !== t16) {
      t17 = <div className="flex items-center gap-4">{t13}{t16}</div>;
      $[33] = t13;
      $[34] = t16;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    const t18 = cn("font-display text-5xl font-bold leading-none tracking-tighter tabular-nums", toneStyles.rankColor);
    const t19 = String(app.rank);
    let t20;
    if ($[36] !== t19) {
      t20 = t19.padStart(2, "0");
      $[36] = t19;
      $[37] = t20;
    } else {
      t20 = $[37];
    }
    let t21;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <ArrowUpRight className="mt-1 h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground" />;
      $[38] = t21;
    } else {
      t21 = $[38];
    }
    const t22 = <div className="flex flex-col items-end"><span className={t18}>{t20}</span>{t21}</div>;
    if ($[39] !== t17 || $[40] !== t22) {
      t7 = <div className="flex items-start justify-between gap-4">{t17}{t22}</div>;
      $[39] = t17;
      $[40] = t22;
      $[41] = t7;
    } else {
      t7 = $[41];
    }
    if ($[42] !== app.meta.description) {
      t8 = <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 min-h-[2.5rem]">{app.meta.description}</p>;
      $[42] = app.meta.description;
      $[43] = t8;
    } else {
      t8 = $[43];
    }
    t4 = "space-y-2 pt-2";
    let t23;
    if ($[44] !== app.totalTokens) {
      t23 = formatTokens(app.totalTokens);
      $[44] = app.totalTokens;
      $[45] = t23;
    } else {
      t23 = $[45];
    }
    let t24;
    if ($[46] !== t23) {
      t24 = <span className="font-display text-3xl font-bold tracking-tight tabular-nums">{t23}</span>;
      $[46] = t23;
      $[47] = t24;
    } else {
      t24 = $[47];
    }
    let t25;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <span className="text-[10px] uppercase tracking-widest text-muted-foreground">tokens processed</span>;
      $[48] = t25;
    } else {
      t25 = $[48];
    }
    if ($[49] !== t24) {
      t5 = <div className="flex items-baseline justify-between">{t24}{t25}</div>;
      $[49] = t24;
      $[50] = t5;
    } else {
      t5 = $[50];
    }
    t3 = "h-2 w-full overflow-hidden rounded-full bg-muted";
    t2 = cn("h-full rounded-full transition-all duration-700 ease-out", toneStyles.bar);
    $[2] = app.meta.Icon;
    $[3] = app.meta.description;
    $[4] = app.meta.displayName;
    $[5] = app.meta.url;
    $[6] = app.rank;
    $[7] = app.source;
    $[8] = app.totalTokens;
    $[9] = tone;
    $[10] = T0;
    $[11] = t10;
    $[12] = t11;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[10];
    t10 = $[11];
    t11 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  const t12 = `${Math.max(2, widthPct)}%`;
  let t13;
  if ($[51] !== t12) {
    t13 = {
      width: t12
    };
    $[51] = t12;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  let t14;
  if ($[53] !== t13 || $[54] !== t2) {
    t14 = <div className={t2} style={t13} />;
    $[53] = t13;
    $[54] = t2;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== t14 || $[57] !== t3) {
    t15 = <div className={t3}>{t14}</div>;
    $[56] = t14;
    $[57] = t3;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== app.totalRequests) {
    t16 = numberFormatter.format(app.totalRequests);
    $[59] = app.totalRequests;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== t16) {
    t17 = <span>{t16} requests</span>;
    $[61] = t16;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== lastUsed) {
    t18 = lastUsed && <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />last seen {lastUsed}</span>;
    $[63] = lastUsed;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t17 || $[66] !== t18) {
    t19 = <div className="flex items-center justify-between text-xs text-muted-foreground">{t17}{t18}</div>;
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== t15 || $[69] !== t19 || $[70] !== t4 || $[71] !== t5) {
    t20 = <div className={t4}>{t5}{t15}{t19}</div>;
    $[68] = t15;
    $[69] = t19;
    $[70] = t4;
    $[71] = t5;
    $[72] = t20;
  } else {
    t20 = $[72];
  }
  let t21;
  if ($[73] !== t20 || $[74] !== t6 || $[75] !== t7 || $[76] !== t8) {
    t21 = <div className={t6}>{t7}{t8}{t20}</div>;
    $[73] = t20;
    $[74] = t6;
    $[75] = t7;
    $[76] = t8;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  let t22;
  if ($[78] !== T0 || $[79] !== t10 || $[80] !== t11 || $[81] !== t21 || $[82] !== t9) {
    t22 = <T0 {...t9} className={t10}>{t11}{t21}</T0>;
    $[78] = T0;
    $[79] = t10;
    $[80] = t11;
    $[81] = t21;
    $[82] = t9;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  return t22;
}
function GridCard(t0) {
  const $ = _c(51);
  const {
    app,
    maxTokens
  } = t0;
  const widthPct = maxTokens > 0 ? app.totalTokens / maxTokens * 100 : 0;
  let t1;
  if ($[0] !== app.lastUsedAt) {
    t1 = formatRelative(app.lastUsedAt);
    $[0] = app.lastUsedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const lastUsed = t1;
  const Wrap = app.meta.url ? "a" : "div";
  let t2;
  if ($[2] !== app.meta.url) {
    t2 = app.meta.url ? {
      href: app.meta.url,
      target: "_blank" as const,
      rel: "noopener noreferrer"
    } : {};
    $[2] = app.meta.url;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const wrapProps = t2;
  let t3;
  if ($[4] !== app.meta.Icon || $[5] !== app.meta.displayName) {
    t3 = <AppLogo Icon={app.meta.Icon} name={app.meta.displayName} />;
    $[4] = app.meta.Icon;
    $[5] = app.meta.displayName;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== app.meta.displayName) {
    t4 = <h3 className="font-display text-base font-semibold tracking-tight truncate">{app.meta.displayName}</h3>;
    $[7] = app.meta.displayName;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== app.source) {
    t5 = <p className="font-mono text-[11px] text-muted-foreground truncate">{app.source}</p>;
    $[9] = app.source;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="min-w-0">{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <div className="flex min-w-0 items-center gap-3">{t3}{t6}</div>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = String(app.rank);
  let t9;
  if ($[17] !== t8) {
    t9 = t8.padStart(2, "0");
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <span className="font-display text-2xl font-bold leading-none tracking-tighter tabular-nums text-muted-foreground/60">{t9}</span>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7) {
    t11 = <div className="flex items-start justify-between gap-3">{t7}{t10}</div>;
    $[21] = t10;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== app.totalTokens) {
    t12 = formatTokens(app.totalTokens);
    $[24] = app.totalTokens;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12) {
    t13 = <span className="font-display text-2xl font-bold tracking-tight tabular-nums">{t12}</span>;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="text-[10px] uppercase tracking-widest text-muted-foreground">tokens</span>;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t13) {
    t15 = <div className="flex items-baseline justify-between">{t13}{t14}</div>;
    $[29] = t13;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = `${Math.max(2, widthPct)}%`;
  let t17;
  if ($[31] !== t16) {
    t17 = <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-foreground/80 transition-all duration-700" style={{
        width: t16
      }} /></div>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== app.totalRequests) {
    t18 = numberFormatter.format(app.totalRequests);
    $[33] = app.totalRequests;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18) {
    t19 = <span>{t18} requests</span>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== lastUsed) {
    t20 = lastUsed && <span>{lastUsed}</span>;
    $[37] = lastUsed;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t19 || $[40] !== t20) {
    t21 = <div className="flex items-center justify-between text-[11px] text-muted-foreground">{t19}{t20}</div>;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t15 || $[43] !== t17 || $[44] !== t21) {
    t22 = <div className="mt-4 space-y-2">{t15}{t17}{t21}</div>;
    $[42] = t15;
    $[43] = t17;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== Wrap || $[47] !== t11 || $[48] !== t22 || $[49] !== wrapProps) {
    t23 = <Wrap {...wrapProps} className="group relative block rounded-xl border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-md">{t11}{t22}</Wrap>;
    $[46] = Wrap;
    $[47] = t11;
    $[48] = t22;
    $[49] = wrapProps;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
}
function ListRow(t0) {
  const $ = _c(44);
  const {
    app,
    maxTokens
  } = t0;
  const widthPct = maxTokens > 0 ? app.totalTokens / maxTokens * 100 : 0;
  let t1;
  if ($[0] !== app.lastUsedAt) {
    t1 = formatRelative(app.lastUsedAt);
    $[0] = app.lastUsedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const lastUsed = t1;
  const Wrap = app.meta.url ? "a" : "div";
  let t2;
  if ($[2] !== app.meta.url) {
    t2 = app.meta.url ? {
      href: app.meta.url,
      target: "_blank" as const,
      rel: "noopener noreferrer"
    } : {};
    $[2] = app.meta.url;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const wrapProps = t2;
  const t3 = String(app.rank);
  let t4;
  if ($[4] !== t3) {
    t4 = t3.padStart(2, "0");
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <span className="font-display text-xl font-bold tabular-nums text-muted-foreground/70 tracking-tighter">{t4}</span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== app.meta.Icon || $[9] !== app.meta.displayName) {
    t6 = <AppLogo Icon={app.meta.Icon} name={app.meta.displayName} size="sm" />;
    $[8] = app.meta.Icon;
    $[9] = app.meta.displayName;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== app.meta.displayName) {
    t7 = <p className="font-display text-sm font-semibold truncate">{app.meta.displayName}</p>;
    $[11] = app.meta.displayName;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== app.source) {
    t8 = <p className="font-mono text-[10px] text-muted-foreground truncate">{app.source}</p>;
    $[13] = app.source;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="min-w-0">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t6 || $[19] !== t9) {
    t10 = <div className="flex min-w-0 items-center gap-3">{t6}{t9}</div>;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== app.totalTokens) {
    t11 = formatTokens(app.totalTokens);
    $[21] = app.totalTokens;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <span className="font-display text-base font-semibold tabular-nums whitespace-nowrap">{t11}</span>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = `${Math.max(2, widthPct)}%`;
  let t14;
  if ($[25] !== t13) {
    t14 = <div className="h-1 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-foreground/70" style={{
        width: t13
      }} /></div>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t14) {
    t15 = <div className="hidden items-center gap-3 sm:flex">{t12}{t14}</div>;
    $[27] = t12;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== app.totalRequests) {
    t16 = numberFormatter.format(app.totalRequests);
    $[30] = app.totalRequests;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16) {
    t17 = <div className="hidden text-right text-xs text-muted-foreground tabular-nums sm:block">{t16}</div>;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const t18 = lastUsed ?? "\u2014";
  let t19;
  if ($[34] !== t18) {
    t19 = <div className="text-right text-xs text-muted-foreground">{t18}</div>;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== Wrap || $[37] !== t10 || $[38] !== t15 || $[39] !== t17 || $[40] !== t19 || $[41] !== t5 || $[42] !== wrapProps) {
    t20 = <Wrap {...wrapProps} className="group grid grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.5fr)_auto] items-center gap-4 border-b border-border/60 px-2 py-4 transition-colors hover:bg-muted/40 last:border-b-0 sm:grid-cols-[3rem_minmax(0,1fr)_minmax(0,2fr)_8rem_5rem]">{t5}{t10}{t15}{t17}{t19}</Wrap>;
    $[36] = Wrap;
    $[37] = t10;
    $[38] = t15;
    $[39] = t17;
    $[40] = t19;
    $[41] = t5;
    $[42] = wrapProps;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
}
export function AppsGrid(t0) {
  const $ = _c(27);
  const {
    apps
  } = t0;
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  let t1;
  if ($[0] !== apps) {
    t1 = apps.map(_temp);
    $[0] = apps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const ranked = t1;
  let t2;
  if ($[2] !== category || $[3] !== query || $[4] !== ranked) {
    const needle = query.trim().toLowerCase();
    t2 = ranked.filter(a_0 => {
      if (category !== "all" && a_0.meta.category !== category) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return a_0.source.toLowerCase().includes(needle) || a_0.meta.displayName.toLowerCase().includes(needle);
    });
    $[2] = category;
    $[3] = query;
    $[4] = ranked;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const filtered = t2;
  const maxTokens = ranked[0]?.totalTokens ?? 0;
  const isFiltered = filtered.length !== ranked.length;
  let t3;
  if ($[6] !== filtered || $[7] !== isFiltered) {
    t3 = isFiltered ? [] : filtered.slice(0, 3);
    $[6] = filtered;
    $[7] = isFiltered;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const podium = t3;
  let t4;
  if ($[9] !== category || $[10] !== filtered || $[11] !== isFiltered || $[12] !== maxTokens || $[13] !== podium || $[14] !== query) {
    const gridApps = isFiltered ? filtered.slice(0, 12) : filtered.slice(3, 12);
    const listApps = isFiltered ? filtered.slice(12) : filtered.slice(12);
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = e => setQuery(e.target.value);
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== query) {
      t7 = <div className="relative flex-1 max-w-md">{t5}<input type="text" value={query} onChange={t6} placeholder={"Search apps\u2026"} className="h-11 w-full rounded-full border bg-background pl-10 pr-4 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/40 focus:ring-2 focus:ring-foreground/10" /></div>;
      $[18] = query;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== category) {
      t8 = CATEGORIES.map(c => <button key={c.value} type="button" onClick={() => setCategory(c.value)} className={cn("rounded-full border px-4 py-1.5 text-sm transition-all", category === c.value ? "border-foreground bg-foreground text-background" : "border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground")}>{c.label}</button>);
      $[20] = category;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== t8) {
      t9 = <div className="flex flex-wrap items-center gap-2">{t8}</div>;
      $[22] = t8;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== t7 || $[25] !== t9) {
      t10 = <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">{t7}{t9}</div>;
      $[24] = t7;
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    t4 = <div className="space-y-12">{t10}{filtered.length === 0 ? <div className="rounded-2xl border border-dashed py-24 text-center"><p className="font-display text-lg font-semibold">No apps found</p><p className="mt-1 text-sm text-muted-foreground">Try a different search or filter.</p></div> : <>{podium.length === 3 && <section className="space-y-4"><div className="flex items-baseline justify-between"><h2 className="font-display text-2xl font-bold tracking-tight">The leaderboard</h2><span className="text-xs uppercase tracking-widest text-muted-foreground">Top 3 by tokens processed</span></div><div className="grid gap-5 lg:grid-cols-12"><div className="lg:col-span-6"><PodiumCard app={podium[0]} maxTokens={maxTokens} tone="gold" /></div><div className="lg:col-span-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-1"><PodiumCard app={podium[1]} maxTokens={maxTokens} tone="silver" /><PodiumCard app={podium[2]} maxTokens={maxTokens} tone="bronze" /></div></div></section>}{gridApps.length > 0 && <section className="space-y-4"><h2 className="font-display text-xl font-semibold tracking-tight text-muted-foreground">{isFiltered ? "Results" : "More apps"}</h2><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{gridApps.map(app => <GridCard key={app.source} app={app} maxTokens={maxTokens} />)}</div></section>}{listApps.length > 0 && <section className="space-y-4"><h2 className="font-display text-xl font-semibold tracking-tight text-muted-foreground">The long tail</h2><div className="rounded-2xl border bg-card overflow-hidden"><div className="hidden grid-cols-[3rem_minmax(0,1fr)_minmax(0,2fr)_8rem_5rem] gap-4 border-b bg-muted/30 px-2 py-3 text-[10px] font-medium uppercase tracking-widest text-muted-foreground sm:grid"><span>#</span><span>App</span><span>Tokens</span><span className="text-right">Requests</span><span className="text-right">Last seen</span></div>{listApps.map(app_0 => <ListRow key={app_0.source} app={app_0} maxTokens={maxTokens} />)}</div></section>}</>}</div>;
    $[9] = category;
    $[10] = filtered;
    $[11] = isFiltered;
    $[12] = maxTokens;
    $[13] = podium;
    $[14] = query;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function _temp(a, idx) {
  return {
    ...a,
    rank: idx + 1,
    meta: getAppMetadata(a.source)
  };
}